import {
  useQuery, useMutation, useQueryClient 
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getDiscrepancies,
  getDiscrepancyMessages,
  createDiscrepancy,
  sendMessage,
  acceptResolution,
  rejectResolution,
  type CreateDiscrepancyPayload,
} from '../api/discrepancy-client';

export function useDiscrepancies(siteId: string) {
  return useQuery({
    queryKey: ['discrepancies', siteId],
    queryFn: () => getDiscrepancies(siteId),
    enabled: !!siteId,
  });
}

export function useDiscrepancyMessages(discrepancyId: string | undefined) {
  return useQuery({
    queryKey: ['discrepancy-messages', discrepancyId],
    queryFn: () => {
      if (!discrepancyId) {
        return Promise.resolve({ data: [] });
      }
      return getDiscrepancyMessages(discrepancyId);
    },
    enabled: !!discrepancyId,
    refetchInterval: 30000,
  });
}

export function useCreateDiscrepancy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateDiscrepancyPayload) => createDiscrepancy(payload),
    onSuccess: () => {
      toast.success('Discrepancy reported successfully');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to report discrepancy: ${error.message}`);
    },
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discrepancyId,
      content,
      attachments,
    }: {
      discrepancyId: string;
      content: string;
      attachments?: File[];
    }) => sendMessage(discrepancyId, content, attachments),
    onSuccess: (_, { discrepancyId }) => {
      queryClient.invalidateQueries({ queryKey: ['discrepancy-messages', discrepancyId] });
    },
    onError: (error) => {
      toast.error(`Failed to send message: ${error.message}`);
    },
  });
}

export function useAcceptResolution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (discrepancyId: string) => acceptResolution(discrepancyId),
    onSuccess: () => {
      toast.success('Resolution accepted');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to accept resolution: ${error.message}`);
    },
  });
}

export function useRejectResolution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discrepancyId,
      reason,
    }: {
      discrepancyId: string;
      reason: string;
    }) => rejectResolution(discrepancyId, reason),
    onSuccess: () => {
      toast.success('Resolution rejected');
      queryClient.invalidateQueries({ queryKey: ['discrepancies'] });
    },
    onError: (error) => {
      toast.error(`Failed to reject resolution: ${error.message}`);
    },
  });
}
